import { useEffect, useRef } from 'react';

import type { DecisionFormData } from './useDecisionFormData';

interface UsePolicySmartInferenceProps {
  formData: DecisionFormData;
  setFormData: (updater: (prev: DecisionFormData) => DecisionFormData) => void;
}

/**
 * Unidirectional smart inference for policy assessments.
 *
 * Direction: individual policy assessment changes → may set policiesCorrect=false (one-way only)
 *
 * - When any individual policy is marked as disagreed (isCorrect=false),
 *   automatically sets the overall policiesCorrect to false.
 * - Changes to policiesCorrect alone (e.g. manual toggle via like/dislike buttons)
 *   do NOT re-trigger this effect — this is intentional, not a gap.
 * - "Mark All Correct" resets all individual assessments, which fires the effect
 *   but the hasDisagreement check prevents any state change.
 *
 * Why unidirectional? Bidirectional enforcement (where policiesCorrect=true would
 * re-clear itself when individual disagreements exist) caused cascading update
 * issues with "Mark All Correct" (AB#61317671). The current design lets auditors
 * manually override via like/dislike buttons without the effect fighting them.
 */
export function usePolicySmartInference({ formData, setFormData }: UsePolicySmartInferenceProps) {
  // Use refs to track previous values and prevent infinite loops
  const prevTriggeredStatesRef = useRef<string>('');
  const prevNonTriggeredStatesRef = useRef<string>('');
  // Use ref to read policiesCorrect without subscribing to changes — prevents feedback loop
  // where "Mark All Correct" setting policiesCorrect=true re-triggers this effect (AB#61317671)
  const policiesCorrectRef = useRef(formData.policiesCorrect);

  policiesCorrectRef.current = formData.policiesCorrect;

  // Smart inference (unidirectional): only fires when individual policy assessments change,
  // NOT when policiesCorrect changes. policiesCorrect is read via ref, not deps.
  useEffect(() => {
    const triggeredStates = formData.triggeredPolicyAssessments.map(a => `${a.policyId}:${a.isCorrect}`).join(',');
    const nonTriggeredStates = formData.nonTriggeredPolicyAssessments.map(a => `${a.policyId}:${a.isCorrect}`).join(',');

    // Check if states have changed to prevent infinite loops
    if (triggeredStates === prevTriggeredStatesRef.current && nonTriggeredStates === prevNonTriggeredStatesRef.current) {
      return;
    }

    // Update refs first
    prevTriggeredStatesRef.current = triggeredStates;
    prevNonTriggeredStatesRef.current = nonTriggeredStates;

    // Check if any policy has disagreement - if so, set overall to false
    const hasDisagreement =
      formData.triggeredPolicyAssessments.some(a => a.isCorrect === false) || formData.nonTriggeredPolicyAssessments.some(a => a.isCorrect === false);

    if (hasDisagreement && policiesCorrectRef.current !== false) {
      setFormData(prev => ({ ...prev, policiesCorrect: false }));
    }
  }, [formData.triggeredPolicyAssessments, formData.nonTriggeredPolicyAssessments, setFormData]);

  // Note: Only "dislike" inference is implemented (individual → overall)
  // Overall assessment is NOT automatically set to "like" to prevent cascading updates.
  // Users must explicitly use the overall like/dislike buttons for positive assessments.
}
